import {
  RND_SECTION_LOAD_SAFETY_SPAN,
  RND_SECTION_LOAD_FIRST_MAX_DELAY,
  RND_SECTION_LOAD_NTH_MAX_DELAY
} from '../constants';

export const helpers = {
  core_hasOwn: Object.prototype.hasOwnProperty,

  // NOTE:
  //   immutAssign({}, {a: 2, b: 3}, {a: 1}) => {a: 1, b: 3}
  //   immutAssign({}, {a: 2, '<b': null}, {a: 1}) => {a: 1}
  //   immutAssign({}, {a: {a1: 1, a2: 2}, b: 3}, {a: {a1: 2}}) => {a: {a1: 2, a2: 2}, b: 3}
  //   immutAssign({}, {a: {a1: 1, a2: 2}, b: 3}, {'>a': {a1: 2}}) => {a: {a1: 2}, b: 3}
  immutAssign (dest, source, value) {
    let index, doOverwrite, result = Object.assign(dest, source);

    for (let i in value) {
      if (value.hasOwnProperty(i)) {
        if (i.substring(-1, 1) === '>') {
          doOverwrite = true;
          index = i.substr(1);
        } else {
          index = i;
        }

        if (i.substring(-1, 1) === '<') {
          delete result[i.substr(1)];
          continue;
        }

        if (helpers.isObject(value[i]) &&
          typeof result[index] !== 'undefined' &&
          !doOverwrite) {
          result[index] = helpers.immutAssign(helpers.isArray(value[i]) ?
            [] : {}, source[index], value[i]);
        } else {
          result[index] = value[i];
        }
      }
    }

    return result;
  },

  deepAssign (...args: any[]) {
    let options, name, src, copy, copyIsArray, clone;
    let target = arguments[0] || {}
      , i = 1
      , length = arguments.length;

    if (typeof target !== 'object' && !helpers.isFunction(target)) {
      target = {};
    }

    if (length === i) {
      target = this;
      --i;
    }

    for (; i < length; i++) {
      options = arguments[i];
      if (options !== null) {
        for (name in options) {
          if (options.hasOwnProperty(name)) {
            src = target[name];
            copy = options[name];

            if (target === copy) {
              continue;
            }

            copyIsArray = helpers.isArray(copy);
            if (copy && (helpers.isPlainObject(copy) || copyIsArray)) {
              if (copyIsArray) {
                copyIsArray = false;
                clone = src && helpers.isArray(src) ? src : [];

              } else {
                clone = src && helpers.isPlainObject(src) ? src : {};
              }

              target[name] = helpers.deepAssign(clone, copy);

            } else if (copy !== undefined) {
              target[name] = copy;
            }
          }
        }
      }
    }

    return target;
  },

  jsonDeepCopy: (o) => {
    return JSON.parse(JSON.stringify(o));
  },

  disabledEventPropagation (e: Event) {
    if (e) {
      if (e.stopPropagation) {
        e.stopPropagation();
      } else if (window.event) {
        window.event.cancelBubble = true;
      }
    }
  },

  isEmptyObject (obj) {
    return Object.keys(obj).length === 0 &&
      obj.constructor === Object;
  },

  getObjectsByKey (key, values) {
    if (!helpers.isObject(values)) {
      return;
    }

    let valuesKeys = Object.keys(values);

    let currents = valuesKeys.reduce((res, k) => {
      if (values[k][key]) {
        res.push(k);
      }
      return res;
    }, []);

    if (currents.length > 0) {
      return currents;
    }
  },

  getNewObjectInDeepCamelCase (oldObj, isArray?) {
    let key, camelKey, newObj, keys = Object.keys(oldObj);
    let n = keys.length;

    if (Array.isArray(oldObj) && !isArray) {
      return helpers.getNewObjectInDeepCamelCase(oldObj, true);
    }

    if (!isArray) {
      newObj = {};
      while (n--) {
        key = keys[n];
        camelKey = key.replace(/_\w/g, (m) => m[1].toUpperCase());
        if (camelKey === 'idRicetta') {
          camelKey = 'id';
        }
        if (typeof oldObj[key] === 'object' && oldObj[key] !== null) {
          newObj[camelKey] = helpers.getNewObjectInDeepCamelCase(
            oldObj[key], Array.isArray(oldObj[key]));
        } else {
          newObj[camelKey] = oldObj[key];
        }
      }
    } else {
      newObj = [];
      for (let i = 0; i < n; i++) {
        if (typeof oldObj[i] === 'object' && oldObj[i] !== null) {
          newObj.push(helpers.getNewObjectInDeepCamelCase(
            oldObj[i], Array.isArray(oldObj[i])));
        } else {
          newObj.push(oldObj[i]);
        }
      }
    }

    return newObj;
  },

  getNewObjectInCamelCase (oldObj) {
    let key, camelKey, keys = Object.keys(oldObj);
    let n = keys.length;
    let newObj = {};

    while (n--) {
      key = keys[n];
      camelKey = key.replace(/_\w/g, (m) => m[1].toUpperCase());
      newObj[camelKey] = oldObj[key];
    }

    return newObj;
  },

  isFunction (value) {
    return typeof value === 'function';
  },

  isObject (value) {
    // http://jsperf.com/isobject4
    return value !== null && typeof value === 'object';
  },

  isArray (value) {
    return Array.isArray(value);
  },

  isWindow (obj) {
    // In IE8 window.window !== window.window, so we allow == here.
    return obj !== null && obj === obj.window;
  },

  isPlainObject (obj) {
    if (!obj || typeof obj !== 'object' ||
      obj.nodeType || helpers.isWindow(obj)) {
      return false;
    }
    try {
      if (obj.constructor &&
        !helpers.core_hasOwn.call(obj, 'constructor') &&
        !helpers.core_hasOwn.call(obj.constructor.prototype, 'isPrototypeOf')) {
        return false;
      }
    } catch (e) {
      return false;
    }

    let key;
    for (key in obj) {
      if (key) {
      }
    }

    return key === undefined || helpers.core_hasOwn.call(obj, key);
  },

  pascalCaseToCamelCase (str) {
    return str.charAt(0).toLowerCase() + str.substring(1);
  },

  camelCaseToPascalCase (str) {
    return str.charAt(0).toUpperCase() + str.substring(1);
  },

  camelCaseToSpaces (str) {
    return str
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toLowerCase() + word.substring(1))
      .join(' ');
  },

  dashCaseToCamelCase (str) {
    return str.replace(/-([a-z])/ig, (all, letter) => {
      return letter.toUpperCase();
    });
  },

  snakeCaseToCamelCase (str) {
    return str.replace(/_([a-z])/ig, (all, letter) => {
      return letter.toUpperCase();
    });
  },

  dashCaseToSpaces (str) {
    return this.camelCaseToSpaces(this.dashCaseToCamelCase(str));
  },

  generateShortUID () {
    return new Date().getTime() + Math.floor(Math.random() * 100000);
  },

  generateLocalID(){
    const localIDLength = 8;
    const uid = helpers.generateUID();
    let indexes = Array.apply(null, {length: uid.length}).map(Number.call, Number);
    let localIDIndexes;
    let localIDString: string = '';

    // shuffle
    indexes.sort(function() { return 0.5 - Math.random() });
    localIDIndexes = indexes.slice(0, localIDLength);

    localIDIndexes.forEach(index => {
      localIDString += uid.charAt(index);
    });

    return localIDString; 
  },

  generateUID (separator?) {
    separator = separator || '-';

    function S4 () {
      return (((1 + Math.random()) * 0x10000) || 0).toString(16).substring(1);
    }

    return (
      S4() + S4() +
      separator + S4() +
      separator + S4() +
      separator + S4() +
      separator + S4() +
      S4() + S4()
    );
  },

  differenceInSecondsBetweensDates (date1: Date, date2: Date): number {
    return Math.abs(
      Math.round(
        (date1.getTime() - date2.getTime()) / 1000
      )
    );
  },

  mmssfromSecs (secs) {
    let hours = Math.floor(secs / 3600)
      , minutes = Math.floor((secs - (hours * 3600)) / 60)
      , seconds = secs - (hours * 3600) - (minutes * 60)
      , output = []
    ;

    seconds = Math.round(seconds * 100) / 100;

    if (hours > 0) {
      output.push(hours < 10 ? '0' + hours : hours);
    }
    output.push(minutes < 10 ? '0' + minutes : minutes);
    output.push(seconds < 10 ? '0' + seconds : seconds);

    return output.join(':');

    /*
    return (hours < 10 ? '0' + hours : hours) + ':' +
           (minutes < 10 ? '0' + minutes : minutes) +
      ':' + (seconds  < 10 ? '0' + seconds : seconds);
      */
  },

  formatDate (date?) {
    date = typeof date === 'undefined' ? new Date() : date;
    let i, strDate = [], strHour = [];
    strDate.push(date.getFullYear());
    strDate.push(date.getMonth());
    strDate.push(date.getDay());

    strHour.push(date.getHours());
    strHour.push(date.getMinutes());
    strHour.push(date.getSeconds());

    for (i = 0; i < 3; i++) {
      strDate[i] = strDate[i] < 10 ? '0' + strDate[i] : strDate[i];
      strHour[i] = strHour[i] < 10 ? '0' + strHour[i] : strHour[i];
    }
    strHour.push(date.getMilliseconds());

    return strDate.join('-') + ' ' + strHour.join(':');
  },

  setCookie (name, value, minutes) {
    let expires;
    if (minutes) {
      let date = new Date();
      date.setTime(date.getTime() + (minutes * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    } else {
      expires = '';
    }
    document.cookie = name + '=' + value + expires + '; path=/';
  },

  getCookie (name) {
    if (document.cookie.length > 0) {
      let c_start = document.cookie.indexOf(name + '=');
      if (c_start !== -1) {
        c_start = c_start + name.length + 1;
        let c_end = document.cookie.indexOf(';', c_start);
        if (c_end === -1) {
          c_end = document.cookie.length;
        }
        return encodeURI(document.cookie.substring(c_start, c_end));
      }
    }
    return '';
  },

  copyToClipboard (msg) {
    let aux = document.createElement("input");
    aux.setAttribute("value", msg);
    document.body.appendChild(aux);
    aux.select();
    document.execCommand("copy");
    // Remove it from the body
    document.body.removeChild(aux);
  },

  getLoadNextRicettaDelay (queue, metadata, progress, isMRFirstAvailable) {
    let maxDelay = isMRFirstAvailable ? RND_SECTION_LOAD_NTH_MAX_DELAY : RND_SECTION_LOAD_FIRST_MAX_DELAY;

    if (typeof queue === 'undefined' || queue.list.length === 0) {
      return 0;
    }

    let idRicetta = queue.list[0].id
      , sectionViewTime = 0
      , range = [0, 0]
      , delay = 0
    ;
    if (typeof progress.sections !== 'undefined') {
      if (typeof progress.sections[idRicetta] !== 'undefined') {
        if (progress.sections[idRicetta].completed) {
          return 0;
        }
        if (typeof progress.sections[idRicetta].viewTime !== 'undefined') {
          sectionViewTime = parseInt(progress.sections[idRicetta].viewTime);
        }
      }
    }

    if (progress.testStartedAtLogin) {
      return 0;
    }
    range[1] = queue.list[0].tempoEsecuzione - sectionViewTime - RND_SECTION_LOAD_SAFETY_SPAN;
    range[1] = range[1] >= maxDelay ? maxDelay : range[1];

    if (range[1] <= range[0]) {
      return 0;
    }

    delay = helpers.getRandomInt(range);

    return delay * 1000;
  },

  getRandomInt (min, max?) {
    if (typeof min === 'object') {
      max = min[1];
      min = min[0];
    }
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
};



// WEBPACK FOOTER //
// ./src/app/helpers/helpers.ts